<style scoped lang="scss">
@import '~variables';

.drop-area {
    min-height: 150px;
    border: 2px dashed $gray-500;
    background-color: $white;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
}

.hidden {
    display: none;
}

.files {
    margin-top: 1rem;

    .details {
        display: flex;
        align-items: center;

        & > * {
            margin-right: 1rem;
        }

        &:hover {
            .delete {
                display: block;
            }
        }

        .image {
            background-repeat: no-repeat;
            background-size: contain;
            background-position: center center;
            border: 1px solid $gray-500;
        }

        .filename {
            word-wrap: anywhere;
        }

        .error {
            color: $red;
        }

        .delete {
            color: $red;
            display: none;
        }
    }

    .progress-background {
        display: flex;
        height: 1rem;
        overflow: hidden;
        font-size: .75rem;
        background-color: #e9ecef;
        border-radius: .25rem;

        .progress-value {
            display: flex;
            flex-direction: column;
            justify-content: center;
            overflow: hidden;
            color: #fff;
            text-align: center;
            white-space: nowrap;
            background-color: #007bff;
            transition: width .6s ease;
            width: 0;
        }
    }
}
</style>

<template>
    <div>
        <div class="drop-area" @drop.prevent="droppedFiles" @dragover.prevent :style="{'min-height': dropAreaMinHeight + 'px'}">
            <div>
                {{ dragText || 'Drag And Drop your file' + (this.multiple ? 's' :'') + ' here' }}
            </div>
            <div>
                <input type="file" ref="file" class="hidden" @change="selectedFiles" :multiple="this.multiple">
                <button type="button" @click="$refs.file.click()">{{ uploadButtonText || 'Upload file' + (this.multiple ? 's' :'') }}</button>
            </div>
        </div>

        <div class="files">
            <div class="file mb-4" v-for="file in files">

                <div class="details">
                    <div v-if="showThumb" class="image" :style="{'background-image': 'url(\'' + file.thumb + '\')', 'width': (thumbWidth ? thumbWidth + 'px' : 'auto'), 'height': (thumbHeight ? thumbHeight + 'px' : 'auto'), 'min-width': (thumbWidth ? thumbWidth + 'px' : null), 'min-height': (thumbHeight ? thumbHeight + 'px' : null)}"></div>
                    <div>
                        <div class="filename">
                            {{ file.name }}<br/>{{ file.size | prettyBytes }}
                        </div>
                        <div class="error">
                            {{ file.error }}
                        </div>
                    </div>
                    <button type="button" class="delete" @click="removeFile(file)">{{ autoUpload ? (deleteText || 'Delete') : (cancelText || 'Cancel') }}</button>
                </div>

                <div class="progress-background mt-2" v-if="!file.error">
                    <div class="progress-value" role="progressbar" :style="{'width': file.progress + '%'}">{{ file.progress }}%</div>
                </div>
            </div>
        </div>

    <button v-if="!autoUpload" @click="start()">{{ startButtonText || 'Upload' }}</button>
</div>
</template>

<script>
    import * as Exif from 'exif-js';

    export default {

        data() {
            return {
                files: [],
                filesUploaded: 0,
                filesComplete: 0,
                promises: [],
            };
        },

        props: {
            url: String,
            data: Object,
            dropAreaMinHeight: {
                type: Number,
                default: 100
            },
            thumbHeight: {
                type: Number,
                default: 80
            },
            thumbWidth: {
                type: Number,
                default: 80
            },
            multiple: {
                type: Boolean,
                default: true
            },
            autoUpload: {
                type: Boolean,
                default: true
            },
            showThumb: {
                type: Boolean,
                default: true
            },
            uploadButtonText: String,
            dragText: String,
            startButtonText: String,
            deleteText: String,
            cancelText: String,
            clearFinished: {
                type: Boolean,
                default: true
            },
            extensionWhitelist: Array,
            extensionBlacklist: Array,
        },

        mounted() {
        },

        methods: {

            selectedFiles(e) {
                this.addFile(e.target.files);
            },

            droppedFiles(e) {
                this.addFile(e.dataTransfer.files);
            },

            addFile(files) {
                if(files) {
                    ([...files]).forEach(file => {

                        var ext = file.name.substr(file.name.lastIndexOf('.') + 1).toLowerCase();
                        if (this.extensionWhitelist && this.extensionWhitelist.indexOf(ext) < 0) {
                            this.$emit('file-rejected', {file: file, error: 'Extension not in white list.'});
                            return;
                        } else if (this.extensionBlacklist && this.extensionBlacklist.indexOf(ext) > -1) {
                            this.$emit('file-rejected', {file: file, error: 'Extension in black list'});
                            return;
                        }

                        file.progress = 0;
                        this.files.push(file);

                        if (this.showThumb) {
                            file.thumb = 'https://cdn.jsdelivr.net/npm/pretty-file-icons@2.2.1/svg/' + prettyFileIcons.getIcon(file.name, 'svg');

                            const reader = new FileReader();
                            reader.onload = (e) => {
                                const img = document.createElement('img');
                                img.src = e.target.result;
                                img.onload = (e) => {
                                    const canvas = document.createElement('canvas');
                                    const MAX_WIDTH = this.thumbWidth * 2;
                                    const MAX_HEIGHT = this.thumbHeight * 2;
                                    let width = img.width;
                                    let height = img.height;

                                    new Promise(resolve => {
                                        Exif.getData(img, function () {
                                            resolve(Exif.getAllTags(this));
                                        });
                                    }).then(exif => {
                                        let rotation = 0;
                                        switch(exif.Orientation) {
                                            case 8:
                                                rotation = 90;
                                                break;
                                            case 3:
                                                rotation = 180;
                                                break;
                                            case 6:
                                                rotation = -90;
                                                break;
                                        }

                                        if (width > height) { // Landscape
                                            if (width > MAX_WIDTH) {
                                                height *= MAX_WIDTH / width;
                                                width = MAX_WIDTH;
                                            }
                                        } else { // Portrait
                                            if (height > MAX_HEIGHT) {
                                                width *= MAX_HEIGHT / height;
                                                height = MAX_HEIGHT;
                                            }
                                        }

                                        if (rotation % 180 === 0) {
                                            canvas.width = width;
                                            canvas.height = height;
                                        } else {
                                            canvas.width = height;
                                            canvas.height = width;
                                        }

                                        const ctx = canvas.getContext('2d', {preserveDrawingBuffer: true});
                                        ctx.save();
                                        ctx.translate(canvas.width / 2 , canvas.height / 2);
                                        ctx.rotate(rotation * Math.PI / 180);
                                        ctx.drawImage(img, -width / 2, -height / 2, width, height);
                                        ctx.restore();

                                        var data = canvas.toDataURL('image/jpeg', 1);
                                        if (data.length > 10) {
                                            file.thumb = data;
                                        }
                                        this.$emit('file-added', {file: file});
                                        this.$forceUpdate();

                                        if (this.autoUpload) {
                                            this.upload(file);
                                        }
                                    });
                                };
                                img.onerror = (e) => {
                                    this.$emit('file-added', {file: file});
                                    this.$forceUpdate();

                                    if (this.autoUpload) {
                                        this.upload(file);
                                    }
                                };

                            }
                            reader.readAsDataURL(file);
                        } else {
                            this.$emit('file-added', {file: file});

                            if (this.autoUpload) {
                                this.upload(file);
                            }
                        }

                    });
                }
            },

            removeFile(file) {
                this.$emit('file-deleted', {file: file});

                if (file.cancelUpload) {
                    file.cancelUpload.cancel('Operation canceled by the user.');
                }

                this.removeFromList(file);
            },

            removeFromList(file) {
                this.files = this.files.filter(f => {
                    return f != file;
                });
            },

            start() {
                this.$emit('files-submitted', {files: files});
                files.forEach(file => {
                    this.upload(file);
                });
            },

            checkPromises() {
                if (this.filesUploaded > 0 && this.filesUploaded === this.filesComplete) {
                    this.filesUploaded = 0;
                    this.filesComplete = 0;
                    this.$emit('upload-completed');
                }
            },

            upload(file) {

                let formData = new FormData();
                formData.append('file', file);

                const CancelToken = axios.CancelToken;
                file.cancelUpload = CancelToken.source();

                this.$emit('upload-started');
                this.filesUploaded++;
                axios.post(
                    '/dynamic/collections/upload' ,
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        },
                        onUploadProgress: (progressEvent) => {
                            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
                            file.progress = percentCompleted;
                            this.$emit('file-progress', {file: file, progressEvent: progressEvent});
                            this.$forceUpdate();
                        },
                        cancelToken: file.cancelUpload.token,
                    },
                )
                .then(response => {

                    this.$emit('file-success', {file: file, data: response.data});

                    if (this.clearFinished) {
                        this.removeFromList(file);
                    }
                    this.$forceUpdate();
                })
                .catch(err => {
                    file.error = err;
                    if (axios.isCancel(err)) {
                        this.$emit('file-canceled', {file: file});
                    } else {
                        this.$emit('file-error', {file: file, error: err});
                    }
                })
                .finally(() => {
                    this.filesComplete++;
                    this.checkPromises();
                    this.$forceUpdate();
                });
            },

        },

    }
</script>
